import { memo, type ReactNode, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import '../styles/gameplay.css';

interface ScoreboardProps {
  blueScore: number;
  redScore: number;
}

const ANIMATION_DURATION_MS = 600;

export const Scoreboard = memo(function Scoreboard({ blueScore, redScore }: ScoreboardProps): ReactNode {
  const t = useT();
  const prevBlueScore = useRef<number>(blueScore);
  const prevRedScore = useRef<number>(redScore);
  const isFirstRender = useRef<boolean>(true);

  const [animateBlue, setAnimateBlue] = useState(false);
  const [animateRed, setAnimateRed] = useState(false);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevBlueScore.current = blueScore;
      prevRedScore.current = redScore;
      return;
    }

    let blueChanged = false;
    let redChanged = false;

    if (blueScore !== prevBlueScore.current) {
      prevBlueScore.current = blueScore;
      blueChanged = true;
    }
    if (redScore !== prevRedScore.current) {
      prevRedScore.current = redScore;
      redChanged = true;
    }

    if (blueChanged) {
      setAnimateBlue(true);
      const timer = setTimeout(() => setAnimateBlue(false), ANIMATION_DURATION_MS);
      return () => clearTimeout(timer);
    }
    if (redChanged) {
      setAnimateRed(true);
      const timer = setTimeout(() => setAnimateRed(false), ANIMATION_DURATION_MS);
      return () => clearTimeout(timer);
    }
  }, [blueScore, redScore]);

  const blueAnimationStyle: React.CSSProperties = animateBlue
    ? { animation: `scorePulse ${ANIMATION_DURATION_MS}ms ease-out, scoreGlowBlue ${ANIMATION_DURATION_MS}ms ease-out` }
    : {};

  const redAnimationStyle: React.CSSProperties = animateRed
    ? { animation: `scorePulse ${ANIMATION_DURATION_MS}ms ease-out, scoreGlowRed ${ANIMATION_DURATION_MS}ms ease-out` }
    : {};

  return (
    <div className="scoreboard" data-testid="scoreboard">
      <div
        className="scoreboard-card scoreboard-card--blue"
        data-testid="scoreboard-blue"
        style={blueAnimationStyle}
      >
        <div className="scoreboard-label scoreboard-label--blue">{t('scoreboard.blue')}</div>
        <div
          className="scoreboard-value scoreboard-value--blue"
          data-testid="scoreboard-blue-value"
        >
          {blueScore}
        </div>
      </div>
      <div
        className="scoreboard-card scoreboard-card--red"
        data-testid="scoreboard-red"
        style={redAnimationStyle}
      >
        <div className="scoreboard-label scoreboard-label--red">{t('scoreboard.red')}</div>
        <div
          className="scoreboard-value scoreboard-value--red"
          data-testid="scoreboard-red-value"
        >
          {redScore}
        </div>
      </div>
    </div>
  );
});
